import {
  CREATED_AT_DATE_FILTER_ID,
  CREATED_AT_DATE_PRESET,
  CREATED_AT_DATE_PRESET_VALUES,
  CREATED_AT_DATE_URL_PARAMS,
  getCreatedAtDatePresetDurationMs,
  type CreatedAtDatePreset,
} from './created-at-date-filter.constants';

export type CreatedAtDateFilterValue = {
  preset: CreatedAtDatePreset | null;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  displayLabel?: string | null;
};

export type CreatedAtDateRange = {
  createdStartDate?: Date;
  createdEndDate?: Date;
};

export type CreatedAtDateFilterPersistenceValue = {
  preset: CreatedAtDatePreset | null;
  createdStartDate: string | null;
  createdEndDate: string | null;
};

const LEGACY_DISCOVERY_DATE_TO_PRESET: Record<string, CreatedAtDatePreset> = {
  lastWeek: CREATED_AT_DATE_PRESET.DAYS_7,
  last30Days: CREATED_AT_DATE_PRESET.DAYS_30,
  last3Months: CREATED_AT_DATE_PRESET.DAYS_90,
  lastYear: CREATED_AT_DATE_PRESET.DAYS_365,
};

const LEGACY_TIME_FRAME_TO_PRESET: Record<string, CreatedAtDatePreset> = {
  '24h': CREATED_AT_DATE_PRESET.DAYS_1,
  '7d': CREATED_AT_DATE_PRESET.DAYS_7,
  '30d': CREATED_AT_DATE_PRESET.DAYS_30,
  '90d': CREATED_AT_DATE_PRESET.DAYS_90,
};

export const isCreatedAtDatePreset = (
  value: string,
): value is CreatedAtDatePreset =>
  (CREATED_AT_DATE_PRESET_VALUES as readonly string[]).includes(value);

const parseDateParam = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date: Date): Date => {
  const nextDate = new Date(date);

  nextDate.setUTCHours(0, 0, 0, 0);

  return nextDate;
};

const endOfDay = (date: Date): Date => {
  const nextDate = new Date(date);

  nextDate.setUTCHours(23, 59, 59, 999);

  return nextDate;
};

export const getClearedCreatedAtDateFilterValue =
  (): CreatedAtDateFilterValue => ({
    preset: null,
    createdStartDate: null,
    createdEndDate: null,
    displayLabel: null,
  });

export const getDefaultCreatedAtDateFilterValue =
  getClearedCreatedAtDateFilterValue;

export const isCreatedAtDateFilterActive = ({
  filter,
}: {
  filter: CreatedAtDateFilterValue;
}): boolean => {
  if (!filter.preset) {
    return false;
  }

  if (filter.preset === CREATED_AT_DATE_PRESET.SINCE_LOGIN) {
    return !!filter.createdStartDate;
  }

  return true;
};

export const hasCreatedAtDateFilterChanged = ({
  left,
  right,
}: {
  left: CreatedAtDateFilterValue;
  right: CreatedAtDateFilterValue;
}): boolean =>
  left.preset !== right.preset ||
  left.createdStartDate?.getTime() !== right.createdStartDate?.getTime() ||
  left.createdEndDate?.getTime() !== right.createdEndDate?.getTime() ||
  (left.displayLabel ?? null) !== (right.displayLabel ?? null);

export const parseCreatedAtDateFilterFromSearchParams = ({
  searchParams,
}: {
  searchParams: Pick<URLSearchParams, 'get'>;
}): CreatedAtDateFilterValue => {
  const presetParam = searchParams.get(CREATED_AT_DATE_URL_PARAMS.preset);
  const legacyDiscoveryDate = searchParams.get('discoveryDate');
  const legacyTimeFrame = searchParams.get('timeFrame');

  const createdStartDate = parseDateParam(
    searchParams.get(CREATED_AT_DATE_URL_PARAMS.startDate),
  );
  const createdEndDate = parseDateParam(
    searchParams.get(CREATED_AT_DATE_URL_PARAMS.endDate),
  );

  if (presetParam === CREATED_AT_DATE_PRESET.SINCE_LOGIN) {
    if (createdStartDate) {
      return {
        preset: CREATED_AT_DATE_PRESET.SINCE_LOGIN,
        createdStartDate,
        createdEndDate: null,
      };
    }

    return getDefaultCreatedAtDateFilterValue();
  }

  if (createdStartDate || createdEndDate) {
    return {
      preset: CREATED_AT_DATE_PRESET.CUSTOM,
      createdStartDate,
      createdEndDate,
    };
  }

  if (presetParam && isCreatedAtDatePreset(presetParam)) {
    return {
      preset: presetParam,
      createdStartDate: null,
      createdEndDate: null,
    };
  }

  if (
    legacyDiscoveryDate &&
    LEGACY_DISCOVERY_DATE_TO_PRESET[legacyDiscoveryDate]
  ) {
    return {
      preset: LEGACY_DISCOVERY_DATE_TO_PRESET[legacyDiscoveryDate],
      createdStartDate: null,
      createdEndDate: null,
    };
  }

  if (legacyTimeFrame && LEGACY_TIME_FRAME_TO_PRESET[legacyTimeFrame]) {
    return {
      preset: LEGACY_TIME_FRAME_TO_PRESET[legacyTimeFrame],
      createdStartDate: null,
      createdEndDate: null,
    };
  }

  return getDefaultCreatedAtDateFilterValue();
};

export const serializeCreatedAtDateFilterForPersistence = ({
  filter,
}: {
  filter: CreatedAtDateFilterValue;
}): CreatedAtDateFilterPersistenceValue => ({
  preset: filter.preset,
  createdStartDate:
    filter.preset === CREATED_AT_DATE_PRESET.SINCE_LOGIN
      ? null
      : (filter.createdStartDate?.toISOString() ?? null),
  createdEndDate: filter.createdEndDate?.toISOString() ?? null,
});

const isCreatedAtDateFilterPersistenceValue = (
  value: unknown,
): value is CreatedAtDateFilterPersistenceValue => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as CreatedAtDateFilterPersistenceValue;

  return (
    (!record.preset ||
      (typeof record.preset === 'string' &&
        isCreatedAtDatePreset(record.preset))) &&
    (record.createdStartDate === null ||
      typeof record.createdStartDate === 'string') &&
    (record.createdEndDate === null ||
      typeof record.createdEndDate === 'string')
  );
};

export const parseCreatedAtDateFilterFromExternalFilters = ({
  externalFilters,
}: {
  externalFilters: Record<string, unknown>;
}): CreatedAtDateFilterValue | undefined => {
  const persistedFilter = externalFilters.createdAtDateFilter;

  if (isCreatedAtDateFilterPersistenceValue(persistedFilter)) {
    return {
      preset: persistedFilter.preset,
      createdStartDate: parseDateParam(persistedFilter.createdStartDate),
      createdEndDate: parseDateParam(persistedFilter.createdEndDate),
    };
  }

  return undefined;
};

export const applyCreatedAtDateFilterToSearchParams = ({
  params,
  filter,
}: {
  params: URLSearchParams;
  filter: CreatedAtDateFilterValue;
}): URLSearchParams => {
  params.delete(CREATED_AT_DATE_URL_PARAMS.preset);
  params.delete(CREATED_AT_DATE_URL_PARAMS.startDate);
  params.delete(CREATED_AT_DATE_URL_PARAMS.endDate);
  params.delete('discoveryDate');
  params.delete('timeFrame');
  params.delete('dateRange');

  if (!filter.preset) {
    return params;
  }

  if (filter.preset === CREATED_AT_DATE_PRESET.CUSTOM) {
    params.set(
      CREATED_AT_DATE_URL_PARAMS.preset,
      CREATED_AT_DATE_PRESET.CUSTOM,
    );
    if (filter.createdStartDate) {
      params.set(
        CREATED_AT_DATE_URL_PARAMS.startDate,
        filter.createdStartDate.toISOString(),
      );
    }

    if (filter.createdEndDate) {
      params.set(
        CREATED_AT_DATE_URL_PARAMS.endDate,
        filter.createdEndDate.toISOString(),
      );
    }

    return params;
  }

  if (filter.preset === CREATED_AT_DATE_PRESET.SINCE_LOGIN) {
    if (filter.createdStartDate) {
      params.set(
        CREATED_AT_DATE_URL_PARAMS.preset,
        CREATED_AT_DATE_PRESET.SINCE_LOGIN,
      );
      params.set(
        CREATED_AT_DATE_URL_PARAMS.startDate,
        filter.createdStartDate.toISOString(),
      );
    }

    return params;
  }

  params.set(CREATED_AT_DATE_URL_PARAMS.preset, filter.preset);

  return params;
};

export const resolveCreatedAtDateRange = ({
  filter,
  now = new Date(),
}: {
  filter: CreatedAtDateFilterValue;
  now?: Date;
}): CreatedAtDateRange => {
  if (!filter.preset) {
    return {};
  }

  if (filter.preset === CREATED_AT_DATE_PRESET.CUSTOM) {
    if (!filter.createdStartDate && !filter.createdEndDate) {
      return {};
    }

    if (filter.createdStartDate && filter.createdEndDate) {
      return {
        createdStartDate: startOfDay(filter.createdStartDate),
        createdEndDate: endOfDay(filter.createdEndDate),
      };
    }

    if (filter.createdStartDate) {
      return {
        createdStartDate: startOfDay(filter.createdStartDate),
        createdEndDate: endOfDay(now),
      };
    }

    if (!filter.createdEndDate) {
      return {};
    }

    return {
      createdEndDate: endOfDay(filter.createdEndDate),
    };
  }

  if (filter.preset === CREATED_AT_DATE_PRESET.SINCE_LOGIN) {
    if (!filter.createdStartDate) {
      return {};
    }

    return {
      createdStartDate: filter.createdStartDate,
      createdEndDate: now,
    };
  }

  const durationMs = getCreatedAtDatePresetDurationMs({
    preset: filter.preset,
  });

  return {
    createdStartDate: new Date(now.getTime() - durationMs),
    createdEndDate: now,
  };
};

export const buildSinceLoginDateFilter = ({
  sinceLoginStartDate,
}: {
  sinceLoginStartDate: Date;
}): CreatedAtDateFilterValue => ({
  preset: CREATED_AT_DATE_PRESET.SINCE_LOGIN,
  createdStartDate: sinceLoginStartDate,
  createdEndDate: null,
});

export const rehydrateRestoredCreatedAtDateFilter = ({
  filter,
  sinceLoginStartDate,
}: {
  filter: CreatedAtDateFilterValue;
  sinceLoginStartDate: Date | null | undefined;
}): CreatedAtDateFilterValue => {
  if (
    filter.preset !== CREATED_AT_DATE_PRESET.SINCE_LOGIN ||
    !sinceLoginStartDate
  ) {
    return filter;
  }

  if (filter.createdStartDate?.getTime() === sinceLoginStartDate.getTime()) {
    return filter;
  }

  return buildSinceLoginDateFilter({ sinceLoginStartDate });
};

export const buildCreatedAtDateFilterForCreatedAt = ({
  createdAt,
  now = new Date(),
}: {
  createdAt: Date | string;
  now?: Date;
}): CreatedAtDateFilterValue => {
  const createdAtDate =
    typeof createdAt === 'string' ? new Date(createdAt) : createdAt;

  return {
    preset: CREATED_AT_DATE_PRESET.CUSTOM,
    createdStartDate: startOfDay(createdAtDate),
    createdEndDate: endOfDay(now),
  };
};

export const appendCreatedAtDateFilterForCreatedAt = ({
  params,
  createdAt,
  now = new Date(),
}: {
  params: URLSearchParams;
  createdAt: Date | string;
  now?: Date;
}): URLSearchParams => {
  const filter = buildCreatedAtDateFilterForCreatedAt({ createdAt, now });

  return applyCreatedAtDateFilterToSearchParams({ params, filter });
};

export const formatCreatedAtDateFilterLabel = ({
  filter,
  formatDate,
  presetLabels,
}: {
  filter: CreatedAtDateFilterValue;
  formatDate: (date: Date) => string;
  presetLabels: Partial<Record<CreatedAtDatePreset, string>>;
}): string => {
  if (filter.displayLabel) {
    return filter.displayLabel;
  }

  if (!filter.preset) {
    return '';
  }

  if (filter.preset !== CREATED_AT_DATE_PRESET.CUSTOM) {
    const presetLabel = presetLabels[filter.preset];

    if (presetLabel) {
      return presetLabel;
    }

    const presetRange = resolveCreatedAtDateRange({ filter });

    if (presetRange.createdStartDate && presetRange.createdEndDate) {
      return `${formatDate(presetRange.createdStartDate)} - ${formatDate(presetRange.createdEndDate)}`;
    }
  }

  if (filter.createdStartDate && filter.createdEndDate) {
    return `${formatDate(filter.createdStartDate)} - ${formatDate(filter.createdEndDate)}`;
  }

  if (filter.createdStartDate) {
    return formatDate(filter.createdStartDate);
  }

  if (filter.createdEndDate) {
    return formatDate(filter.createdEndDate);
  }

  return presetLabels[CREATED_AT_DATE_PRESET.CUSTOM] ?? '';
};

export const didSelectNewCreatedAtDatePreset = ({
  filterValue,
  previousPreset,
}: {
  filterValue: Array<{ id: string; value: string[] }>;
  previousPreset: string | undefined;
}): boolean => {
  const nextPreset = filterValue.find(
    (entry) => entry.id === CREATED_AT_DATE_FILTER_ID,
  )?.value[0];

  return Boolean(nextPreset) && nextPreset !== previousPreset;
};
